import React, { useState, useEffect } from 'react';
import {
  Box,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField, CompactFilterIconButton } from '../../design-system';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import type { FilterConfig, TableFilters, ToolbarAction } from './types';

function isMeaningfulFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return normalized !== '' && normalized !== 'all';
}

interface EnhancedTableToolbarProps {
  searchable: boolean;
  searchTerm: string;
  searchPlaceholder: string;
  onSearchChange?: (term: string) => void;
  searchDebounceMs: number;
  filters: TableFilters;
  filterOptions: FilterConfig[];
  filterDefaultValues?: TableFilters;
  onFilterChange?: (filters: TableFilters) => void;
  onRefresh?: () => void;
  refreshTitle?: string;
  toolbarActions: ToolbarAction[];
  toolbarLeftContent?: React.ReactNode;
  exportable: boolean;
  onExport?: () => void;
}

const EnhancedTableToolbar: React.FC<EnhancedTableToolbarProps> = ({
  searchable,
  searchTerm,
  searchPlaceholder,
  onSearchChange,
  searchDebounceMs,
  filters,
  filterOptions,
  filterDefaultValues,
  onFilterChange,
  onRefresh,
  refreshTitle = 'Refresh',
  toolbarActions,
  toolbarLeftContent,
  exportable,
  onExport,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearchChange && localSearchTerm !== searchTerm) {
        onSearchChange(localSearchTerm);
      }
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchDebounceMs, onSearchChange, searchTerm]);

  // Sync external searchTerm changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    onSearchChange?.('');
  };

  const handleFilterChange = (field: string, value: unknown) => {
    if (onFilterChange) {
      onFilterChange({
        ...filters,
        [field]: value,
      });
    }
  };

  const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFilterAnchorEl(null);
  };

  const activeFilterCount = Object.entries(filters).filter(([field, value]) => {
    if (!isMeaningfulFilterValue(value)) {
      return false;
    }
    if (filterDefaultValues && field in filterDefaultValues) {
      return String(value) !== String(filterDefaultValues[field]);
    }
    return true;
  }).length;

  const hasRightControls =
    filterOptions.length > 0 ||
    toolbarActions.length > 0 ||
    Boolean(onRefresh) ||
    (exportable && Boolean(onExport));

  return (
    <Box sx={{ mb: 2, pt: 0.5, width: '100%', bgcolor: 'transparent', flexShrink: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: '100%' }}>
        {toolbarLeftContent}

        {/* Search */}
        {searchable && (
          <AppTextField
            size="small"
            placeholder={searchPlaceholder}
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                backgroundImage: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: localSearchTerm && (
                <InputAdornment position="end">
                  <AppIconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </AppIconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {hasRightControls && (
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0} sx={{ ml: 'auto' }}>
            {filterOptions.length > 0 && (
              <>
                <CompactFilterIconButton
                  onClick={handleOpenFilters}
                  activeCount={activeFilterCount}
                  open={Boolean(filterAnchorEl)}
                />

                <Popover
                  open={Boolean(filterAnchorEl)}
                  anchorEl={filterAnchorEl}
                  onClose={handleCloseFilters}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Box sx={{ p: 2, minWidth: 250 }}>
                    <Stack spacing={2}>
                      {filterOptions.map((filter) => {
                        const currentValue = String(filters[filter.field] ?? '');
                        const isEmpty = currentValue === '';
                        const includeEmptyOption = filter.includeEmptyOption !== false;
                        const hasExplicitAllOption =
                          !includeEmptyOption ||
                          filter.options?.some(
                            (option) => option.value === '' || option.value === 'all',
                          );
                        return (
                          <FormControl key={filter.field} fullWidth size="small">
                            <InputLabel shrink>{filter.label}</InputLabel>
                            <Select
                              displayEmpty
                              notched
                              value={currentValue}
                              onChange={(e) => handleFilterChange(filter.field, e.target.value)}
                              label={filter.label}
                              renderValue={(selected) => {
                                if (selected === '' || selected === undefined || selected === null) {
                                  return <em style={{ color: 'inherit', fontStyle: 'normal', opacity: 0.6 }}>All</em>;
                                }
                                const selectedOption = filter.options?.find((option) => option.value === selected);
                                return selectedOption?.label ?? String(selected);
                              }}
                              sx={isEmpty ? { color: 'text.secondary' } : undefined}
                            >
                              {!hasExplicitAllOption && <MenuItem value="">All</MenuItem>}
                              {filter.options?.map((option) => (
                                <MenuItem
                                  key={String(option.value)}
                                  value={option.value === null ? '' : typeof option.value === 'boolean' ? String(option.value) : option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      })}
                    </Stack>
                  </Box>
                </Popover>
              </>
            )}

            {toolbarActions.map((action) => (
              <AppButton
                key={action.label}
                size="small"
                variant={action.variant ?? 'outlined'}
                color={action.color ?? 'primary'}
                startIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </AppButton>
            ))}

            {onRefresh && (
              <AppIconButton onClick={onRefresh} size="small" title={refreshTitle} aria-label={refreshTitle}>
                <RefreshIcon />
              </AppIconButton>
            )}

            {exportable && onExport && (
              <AppIconButton onClick={onExport} size="small" title="Export">
                <ExportIcon />
              </AppIconButton>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default EnhancedTableToolbar;
